import { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FieldText } from "./Customers";

export default function Settings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/settings"); setForm(data); }
    catch { toast.error("Failed to load settings"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      payload.invoice_seq = Number(payload.invoice_seq) || 1000;
      payload.default_vat_rate = Number(payload.default_vat_rate) || 25;
      payload.invoice_payment_terms_days = Number(payload.invoice_payment_terms_days) || 14;
      await api.put("/settings", payload);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <AdminLayout title="Settings"><div className="text-[color:var(--ivory-muted)]">Loading…</div></AdminLayout>;
  }

  const set = (k) => (v) => setForm({ ...form, [k]: v });

  return (
    <AdminLayout title="Company Settings">
      <form onSubmit={save} className="space-y-8 max-w-4xl" data-testid="settings-form">
        <Section title="Identity">
          <div className="grid sm:grid-cols-2 gap-3">
            <FieldText label="Company name" value={form.company_name} onChange={set("company_name")} />
            <FieldText label="Organisationsnummer" value={form.org_number} onChange={set("org_number")} />
            <FieldText label="VAT / Momsnummer" value={form.vat_number} onChange={set("vat_number")} />
            <FieldText label="Website" value={form.website} onChange={set("website")} />
            <FieldText label="Email" value={form.email} onChange={set("email")} />
            <FieldText label="Phone" value={form.phone} onChange={set("phone")} />
          </div>
        </Section>

        <Section title="Address">
          <div className="grid sm:grid-cols-2 gap-3">
            <FieldText label="Address line 1" value={form.address_line1} onChange={set("address_line1")} />
            <FieldText label="Address line 2" value={form.address_line2} onChange={set("address_line2")} />
            <FieldText label="Postal code" value={form.postal_code} onChange={set("postal_code")} />
            <FieldText label="City" value={form.city} onChange={set("city")} />
            <FieldText label="Country" value={form.country} onChange={set("country")} />
          </div>
        </Section>

        <Section title="Banking (printed on invoice)">
          <div className="grid sm:grid-cols-3 gap-3">
            <FieldText label="Bank name" value={form.bank_name} onChange={set("bank_name")} />
            <FieldText label="IBAN" value={form.iban} onChange={set("iban")} />
            <FieldText label="BIC / SWIFT" value={form.bic} onChange={set("bic")} />
          </div>
        </Section>

        <Section title="Invoices">
          <div className="grid sm:grid-cols-3 gap-3">
            <FieldText label="Invoice prefix" value={form.invoice_prefix} onChange={set("invoice_prefix")} />
            <FieldText label="Next sequence" type="number" value={form.invoice_seq} onChange={set("invoice_seq")} />
            <FieldText label="Default VAT %" type="number" value={form.default_vat_rate} onChange={set("default_vat_rate")} />
            <FieldText label="Payment terms (days)" type="number" value={form.invoice_payment_terms_days} onChange={set("invoice_payment_terms_days")} />
          </div>
          <div className="mt-3">
            <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2 block">Footer note on PDF</label>
            <textarea value={form.invoice_footer_note || ""} onChange={(e) => set("invoice_footer_note")(e.target.value)} rows={3} className="email-input pl-4 py-3" style={{ borderRadius: 12, resize: "vertical" }} />
          </div>
        </Section>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="pill pill-primary disabled:opacity-50" data-testid="settings-save">
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.02]">
      <div className="text-xs uppercase tracking-[0.28em] text-[color:var(--ivory-muted)] mb-4">{title}</div>
      {children}
    </div>
  );
}
